/**
 * Final step — "Start your free trial": billing period + the confirm action.
 *
 * Card capture is Stripe-hosted: "Start 14-day trial" POSTs the whole wizard
 * payload to `/self-service/confirm/` (org creation, sign-in integration
 * connect, invites) and hands the browser to the returned Checkout URL. No
 * payment details ever touch the SPA.
 */
import { Lock01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/lib/base/buttons/button';
import { HintText } from '@/lib/base/forms';
import { RadioCard, RadioGroup } from '@/lib/base/radio-buttons';
import {
  buildConfirmPayload,
  confirmErrorMessage,
  useConfirmSelfService,
} from '@/lib/self-service/api';
import type { BillingPeriod } from '@/lib/self-service/constants';
import {
  BILLING_OPTIONS,
  selfServiceStepPath,
} from '@/lib/self-service/constants';
import { useSelfServiceFlowState } from '@/lib/self-service/flowState';

const CREW_STEP_PATH = selfServiceStepPath('crew');

export function TrialStep() {
  const { state, updateState } = useSelfServiceFlowState();
  const { mutate, isPending, isSuccess, isError, error } =
    useConfirmSelfService();

  const handleBillingChange = useCallback(
    // Values come from BILLING_OPTIONS, so the narrowing cast is safe.
    (value: string) => updateState({ billingPeriod: value as BillingPeriod }),
    [updateState]
  );

  const payloadResult = useMemo(() => buildConfirmPayload(state), [state]);

  const handleStartTrial = useCallback(() => {
    if (!payloadResult.ok) {
      return;
    }
    mutate(payloadResult.payload, {
      // Stripe-hosted Checkout is a full-page navigation out of the SPA.
      onSuccess: data => window.location.assign(data.checkout_url),
    });
  }, [mutate, payloadResult]);

  return (
    <div className='flex flex-col gap-6'>
      <RadioGroup
        value={state.billingPeriod}
        onChange={handleBillingChange}
        aria-label='Billing period'
        className='grid grid-cols-1 gap-3 sm:grid-cols-2'
      >
        {BILLING_OPTIONS.map(option => (
          <RadioCard key={option.value} value={option.value} variant='button'>
            {option.label}
          </RadioCard>
        ))}
      </RadioGroup>

      {!payloadResult.ok && (
        <HintText isInvalid showError>
          Add your company name and team size on{' '}
          <Link to={CREW_STEP_PATH} className='underline'>
            step 1
          </Link>{' '}
          to start your trial.
        </HintText>
      )}

      <div className='flex flex-col gap-4'>
        <Button
          size='lg'
          className='w-full'
          onClick={handleStartTrial}
          isDisabled={!payloadResult.ok}
          isLoading={isPending || isSuccess}
          showTextWhileLoading
        >
          Start 14-day trial
        </Button>
        {isError && (
          <HintText isInvalid showError className='text-center'>
            {confirmErrorMessage(error)}
          </HintText>
        )}
        <p className='flex items-center justify-center gap-2 text-sm text-[var(--color-text-tertiary)]'>
          <Lock01 aria-hidden className='size-4' />
          You'll add a card on Stripe's secure checkout. We'll email you 3 days
          before your trial ends.
        </p>
      </div>
    </div>
  );
}
